import React, { useCallback } from 'react';
import type { FC } from 'react';
import { css } from '@compiled/react';

import Button from '@atlaskit/button/new';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { getActionItemIcon } from './actionItemIcon.tsx';
import type { MeetingActionItem } from './meetingTypes.ts';

const rowStyle = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.100'),
	paddingTop: token('space.075'),
	paddingBottom: token('space.075'),
	paddingInline: token('space.100'),
	borderRadius: token('radius.small'),
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});

const leadingStyle = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	minWidth: 0,
	flex: '1 1 auto',
});

const contentStyle = css({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	minWidth: 0,
});

const ctaStyle = css({
	flex: '0 0 auto',
});

type ActionItemProps = {
	item: MeetingActionItem;
	/**
	 * Invoked when the 1-click CTA is clicked. In the shell this is a stub; the
	 * real handler wires the M1CA payload into the action-execution system once
	 * the payload source is confirmed.
	 */
	onActionClick?: (item: MeetingActionItem) => void;
};

/**
 * A single action item row.
 *
 * This is the deliberate isolation boundary for CTA volatility: it renders the
 * item text (+ optional timestamp) and, ONLY when a payload (`item.action`) is
 * present, a 1-click CTA button. Items without a payload render as plain text.
 * The surrounding panel/card never depends on whether a CTA exists.
 */
export const ActionItem: FC<ActionItemProps> = ({ item, onActionClick }) => {
	const handleClick = useCallback(() => onActionClick?.(item), [onActionClick, item]);

	return (
		<div css={rowStyle} data-testid="meeting-action-item">
			<div css={leadingStyle}>
				{getActionItemIcon(item)}
				<div css={contentStyle}>
					<Text>{item.content}</Text>
				</div>
			</div>
			{item.action ? (
				<div css={ctaStyle}>
					<Button
						appearance="default"
						spacing="compact"
						onClick={handleClick}
						testId="meeting-action-item-cta"
					>
						{item.action.label}
					</Button>
				</div>
			) : null}
		</div>
	);
};
